import { conceptoTareoAplicaACanal } from './ajusteConceptoDestino';

/** Códigos PLAME de gratificación (iguales a liquidaciones). */
export const CODIGO_PLAME_GRATIFICACION = '0406';
/** Bonificación extraordinaria proporcional Ley (código operativo en liquidaciones). */
export const CODIGO_PLAME_BONIF_EXTRAORDINARIA = '0313';
/** Variante legacy / catálogo cercano (aparece en algunas planillas). */
export const CODIGO_PLAME_BONIF_EXTRAORDINARIA_ALT = '0312';

export const CODIGOS_GRATIFICACION_PLAME = [
  CODIGO_PLAME_GRATIFICACION,
  CODIGO_PLAME_BONIF_EXTRAORDINARIA,
  CODIGO_PLAME_BONIF_EXTRAORDINARIA_ALT,
];

function textoConceptoPlano(concepto) {
  return String(
    concepto?.descripcion
      || concepto?.concepto
      || concepto?.nombre
      || concepto?.plame_configuracion?.descripcion
      || ''
  )
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Concepto de liquidación en planilla: se informa en boleta/REM (como grati), no entra al neto a pagar.
 */
export function esConceptoLiquidacionPlanilla(concepto) {
  if (!concepto) return false;
  const origen = concepto.origenCalculo || concepto.origen_calculo;
  if (origen !== 'liquidacion') return false;
  return !esDescuentoLiquidacionPlanilla(concepto);
}

/** Descuento PLAME generado por liquidación (p. ej. pensión sobre vacaciones truncas). */
export function esCalculoConceptoLiquidacionDescuento(calculoConcepto) {
  const t = String(calculoConcepto?.tipo_concepto || '').toLowerCase();
  return t.includes('descuento');
}

export function esDescuentoLiquidacionPlanilla(concepto) {
  if (!concepto) return false;
  const origen = concepto.origenCalculo || concepto.origen_calculo;
  if (origen !== 'liquidacion') return false;
  const t = String(concepto.tipo_concepto || '').toLowerCase();
  if (t.includes('descuento')) return true;
  const cod = String(
    concepto.codigoPlame || concepto.codigo || concepto.codigo_plame || ''
  ).replace(/\D/g, '').padStart(4, '0');
  return ['0601', '0602', '0603', '0604', '0605', '0606', '0608'].includes(cod);
}

/** Descuento informado en planilla/REM que no reduce el neto a pagar del mes. */
export function esDescuentoSoloDeclaracion(concepto) {
  return esDescuentoLiquidacionPlanilla(concepto);
}

/**
 * Viático que sí se paga en boleta (no es el 0917 informativo).
 * En planillas cerradas a veces llega con excluir/origen viatico por error.
 */
export function esConceptoViaticoPagado(concepto) {
  if (!concepto) return false;
  const desc = textoConceptoPlano(concepto);
  return desc.includes('viatico') && desc.includes('pagado');
}

/**
 * Declaración de viáticos (tareo / viáticos standard): se informa en planilla/REM
 * pero no afecta el neto a pagar / boleta ordinaria.
 * Código habitual PLAME: 0917 CONDICIONES DE TRABAJO.
 */
export function esConceptoDeclaracionViatico(concepto) {
  if (!concepto) return false;
  if (esConceptoLiquidacionPlanilla(concepto)) return false;
  if (esConceptoViaticoPagado(concepto)) return false;
  const origen = String(concepto.origenCalculo || concepto.origen_calculo || concepto.origen || '');
  if (origen === 'viatico' || origen === 'viatico_declaracion' || origen.includes('viatico')) {
    return true;
  }
  const codigo = String(
    concepto.codigoPlame
      || concepto.codigo
      || concepto.codigo_plame
      || concepto.plame_configuracion?.codigo_plame
      || ''
  );
  // 0917 = CONDICIONES DE TRABAJO (viáticos standard)
  if (codigo === '0917') return true;
  if (textoConceptoPlano(concepto).includes('condiciones de trabajo')) return true;
  return false;
}

/**
 * Declaración de gratificaciones / PLAME (módulo de gratificaciones).
 * No usar solo el flag excluir: en planilla cerrada los viáticos pagados
 * también vienen con excluir y no son grati.
 */
export function esConceptoDeclaracionGratificacion(concepto) {
  if (!concepto) return false;
  if (esConceptoLiquidacionPlanilla(concepto)) return false;
  if (esConceptoViaticoPagado(concepto)) return false;
  if (esConceptoDeclaracionViatico(concepto)) return false;
  const origen = String(concepto.origenCalculo || concepto.origen_calculo || '').toLowerCase();
  if (origen === 'gratificacion' || origen === 'gratificacion_declaracion' || origen.includes('gratificacion')) {
    return true;
  }
  const codigo = String(
    concepto.codigoPlame
      || concepto.codigo
      || concepto.codigo_plame
      || concepto.plame_configuracion?.codigo_plame
      || ''
  );
  if (!CODIGOS_GRATIFICACION_PLAME.includes(codigo)) return false;
  return Boolean(concepto.excluirBoletaOrdinaria || concepto.excluir_boleta_ordinaria);
}

/** Movilidad 0909 que trajo Jatsa (consideraciones destino PLAME). No es la 0909 de tareo ni CONSU. */
export const CODIGO_PLAME_MOVILIDAD_JATSA = '0909';
export const ORIGEN_MOVILIDAD_JATSA = 'jatsa_movilidad';

function codigoDeConceptoPlame(concepto) {
  return String(
    concepto?.codigoPlame
    || concepto?.codigo
    || concepto?.codigo_plame
    || concepto?.plame_configuracion?.codigo_plame
    || ''
  );
}

function codigoPlameNormalizado(cod) {
  const digits = String(cod ?? '').replace(/\D/g, '') || '0';
  return digits.padStart(4, '0');
}

/** Descripción PLAME desde catálogo (ingresos/descuentos). */
export function descripcionPlameFromCatalogo(codigo, catalogo) {
  const codNorm = codigoPlameNormalizado(codigo);
  const listas = [];
  if (Array.isArray(catalogo)) listas.push(catalogo);
  else if (catalogo && typeof catalogo === 'object') listas.push(Object.values(catalogo));

  for (const list of listas) {
    const hit = (list || []).find(
      (c) => codigoPlameNormalizado(c.codigo_plame || c.codigo || c.codigoPlame) === codNorm
    );
    if (!hit) continue;
    if (hit.descripcion) return String(hit.descripcion).trim();
    if (hit.nombre) return String(hit.nombre).trim();
    if (hit.plame_configuracion?.descripcion) return String(hit.plame_configuracion.descripcion).trim();
    if (hit.concepto) return String(hit.concepto).trim();
  }
  return null;
}

export function descripcionPlameParaLinea(codigo, cc, catalogoPlame) {
  const codNorm = codigoPlameNormalizado(codigo);
  const raw = String(cc?.descripcion || cc?.concepto || cc?.nombre || '').trim();
  const rawEsCodigo = !raw || raw === codNorm || codigoPlameNormalizado(raw) === codNorm;
  if (!rawEsCodigo) return raw;
  return descripcionPlameFromCatalogo(codigo, catalogoPlame) || codNorm;
}

/** Texto visible en boleta (sin prefijo numérico PLAME). */
export function etiquetaVisibleConceptoPlame(concepto, catalogo) {
  const cod = concepto?.codigoPlame || concepto?.codigo || concepto?.codigo_plame || '';
  let nombre = descripcionPlameParaLinea(cod, concepto, catalogo);
  return String(nombre).replace(/^\d{3,4}\s*[-·.]\s*/, '').trim() || 'Concepto';
}

function origenConceptoPlano(concepto) {
  return String(
    concepto?.origenCalculo
    || concepto?.origen_calculo
    || concepto?.origen
    || ''
  ).toLowerCase();
}

export function origenEsTareo(concepto) {
  const origen = origenConceptoPlano(concepto);
  return origen === 'tareo' || origen.includes('tareo');
}

function destinoAjusteVaAPlame(concepto) {
  const dest = concepto?.destino_ajuste ?? concepto?.destino;
  if (dest == null || dest === '') return false;
  return String(dest).toLowerCase() !== 'interna';
}

export function esConceptoOrigenJatsa0909(concepto) {
  if (concepto?.origen_jatsa === true || concepto?.origenJatsa === true) {
    return true;
  }
  const origen = origenConceptoPlano(concepto);
  return origen === ORIGEN_MOVILIDAD_JATSA || origen.includes('jatsa_movilidad');
}

/**
 * Solo la 0909 del módulo satélite (Jatsa): se informa y no entra al pago.
 * La 0909 añadida en ajustes posteriores o de tareo/ficha sí es remunerativa.
 */
export function esConceptoDeclaracionMovilidadJatsa(concepto) {
  if (!concepto || esConceptoLiquidacionPlanilla(concepto)) return false;
  if (origenEsTareo(concepto)) return false;
  if (codigoDeConceptoPlame(concepto) !== CODIGO_PLAME_MOVILIDAD_JATSA) return false;
  if (!esConceptoOrigenJatsa0909(concepto)) return false;
  if (concepto.excluirBoletaOrdinaria || concepto.excluir_boleta_ordinaria) return true;
  return destinoAjusteVaAPlame(concepto);
}

/** Ingreso informado en planilla/REM que no forma parte del neto a pagar. */
export function esConceptoSoloDeclaracion(concepto) {
  if (!concepto) return false;
  if (esConceptoLiquidacionPlanilla(concepto)) return true;
  if (esConceptoViaticoPagado(concepto)) return false;
  if (concepto.excluirBoletaOrdinaria || concepto.excluir_boleta_ordinaria) return true;
  return esConceptoDeclaracionGratificacion(concepto)
    || esConceptoDeclaracionViatico(concepto)
    || esConceptoDeclaracionMovilidadJatsa(concepto);
}

function destinoViaticoVaAPlame(concepto) {
  const dest = String(concepto?.destino_ajuste || concepto?.destino || 'plame').toLowerCase();
  return dest !== 'interna';
}

function codigoDeViatico(concepto) {
  return codigoDeConceptoPlame(concepto);
}

/**
 * 0917 de Jatsa/ajustes destino PLAME: se informa en la boleta oficial
 * y no entra al pago. Si el tareo ya lo trajo, se conserva/actualiza el monto.
 */
export function upsertViaticoDeclaracionEnIngresosPlame(ingresos = [], ajustes = [], extras = {}) {
  const out = Array.isArray(ingresos) ? [...ingresos] : [];
  const extraCampos = typeof extras.ingresoSinDescuentoLicGoceHaber === 'function'
    ? extras.ingresoSinDescuentoLicGoceHaber()
    : {};

  (ajustes || []).forEach((concepto) => {
    if (!esConceptoDeclaracionViatico(concepto) && codigoDeViatico(concepto) !== '0917') {
      return;
    }
    if (!destinoViaticoVaAPlame(concepto)) return;
    const monto = parseFloat(concepto.monto) || 0;
    if (monto <= 0) return;
    const codigo = codigoDeViatico(concepto) || '0917';
    const plameId = concepto.plame_configuracion?.id
      || concepto.plame_configuracion_id
      || concepto.id
      || null;
    const descripcion = concepto.plame_configuracion?.descripcion
      || concepto.descripcion
      || concepto.concepto
      || concepto.nombre
      || 'CONDICIONES DE TRABAJO';
    const fila = {
      codigoPlame: codigo,
      codigo,
      codigo_plame: codigo,
      concepto: descripcion,
      descripcion,
      id: plameId,
      monto,
      plame_configuracion_id: plameId,
      tipo_concepto: 'ingreso',
      origenCalculo: 'viatico',
      origen_calculo: 'viatico',
      excluirBoletaOrdinaria: true,
      excluir_boleta_ordinaria: true,
      destino_ajuste: 'plame',
      ...extraCampos,
    };
    const idx = out.findIndex((ing) => String(ing.codigoPlame || ing.codigo || ing.codigo_plame || '') === codigo);
    if (idx >= 0) {
      out[idx] = { ...out[idx], ...fila, monto };
      return;
    }
    out.push(fila);
  });

  return out;
}

/**
 * 0909 de Jatsa (consideraciones destino PLAME): se informa y no entra al pago.
 * No pisa una 0909 que ya venía del tareo / ficha pagada.
 */
export function upsertMovilidadJatsaDeclaracionEnIngresosPlame(ingresos = [], ajustes = [], extras = {}) {
  const out = Array.isArray(ingresos) ? [...ingresos] : [];
  const extraCampos = typeof extras.ingresoSinDescuentoLicGoceHaber === 'function'
    ? extras.ingresoSinDescuentoLicGoceHaber()
    : {};

  (ajustes || []).forEach((concepto) => {
    if (codigoDeConceptoPlame(concepto) !== CODIGO_PLAME_MOVILIDAD_JATSA) return;
    if (!esConceptoOrigenJatsa0909(concepto)) return;
    if (!destinoAjusteVaAPlame(concepto)) return;
    if (origenEsTareo(concepto)) return;
    const monto = parseFloat(concepto.monto) || 0;
    if (monto <= 0) return;
    const plameId = concepto.plame_configuracion?.id
      || concepto.plame_configuracion_id
      || concepto.id
      || null;
    const descripcion = concepto.plame_configuracion?.descripcion
      || concepto.descripcion
      || concepto.concepto
      || concepto.nombre
      || 'MOVILIDAD SUPEDITADA A ASISTENCIA Y QUE CUBRE SÓLO EL TRASLADO';
    const fila = {
      codigoPlame: CODIGO_PLAME_MOVILIDAD_JATSA,
      codigo: CODIGO_PLAME_MOVILIDAD_JATSA,
      codigo_plame: CODIGO_PLAME_MOVILIDAD_JATSA,
      concepto: descripcion,
      descripcion,
      id: plameId,
      monto,
      plame_configuracion_id: plameId,
      tipo_concepto: 'ingreso',
      origenCalculo: ORIGEN_MOVILIDAD_JATSA,
      origen_calculo: ORIGEN_MOVILIDAD_JATSA,
      excluirBoletaOrdinaria: true,
      excluir_boleta_ordinaria: true,
      destino_ajuste: 'plame',
      ...extraCampos,
    };
    const idx = out.findIndex((ing) => {
      if (codigoDeConceptoPlame(ing) !== CODIGO_PLAME_MOVILIDAD_JATSA) return false;
      if (origenEsTareo(ing)) return false;
      return esConceptoDeclaracionMovilidadJatsa(ing)
        || Boolean(ing.excluirBoletaOrdinaria || ing.excluir_boleta_ordinaria);
    });
    if (idx >= 0) {
      out[idx] = { ...out[idx], ...fila, monto };
      return;
    }
    out.push(fila);
  });

  return out;
}

export function filtrarConceptosBoletaOrdinaria(conceptos = []) {
  return (conceptos || []).filter((c) => !esConceptoSoloDeclaracion(c));
}

export function filtrarDescuentosBoletaOrdinaria(conceptos = []) {
  return (conceptos || []).filter((c) => !esDescuentoSoloDeclaracion(c));
}

export function separarIngresosPlanillaYDeclaracion(ingresos = []) {
  const lista = ingresos || [];
  const planilla = [];
  const declaracionGrati = [];
  const declaracionViatico = [];
  lista.forEach((c) => {
    if (esConceptoDeclaracionViatico(c)) {
      declaracionViatico.push(c);
    } else if (esConceptoDeclaracionGratificacion(c)) {
      declaracionGrati.push(c);
    } else if (esConceptoDeclaracionMovilidadJatsa(c) || esConceptoSoloDeclaracion(c)) {
      declaracionViatico.push(c);
    } else {
      planilla.push(c);
    }
  });
  const sum = (arr) => arr.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);
  const declaracion = [...declaracionGrati, ...declaracionViatico];
  return {
    planilla,
    declaracion,
    declaracionGrati,
    declaracionViatico,
    totalPlanilla: sum(planilla),
    totalDeclaracion: sum(declaracion),
    totalDeclaracionGrati: sum(declaracionGrati),
    totalDeclaracionViatico: sum(declaracionViatico),
  };
}

/** Descuentos de boleta vs liquidación (pensión vac. truncas, no neto). */
export function separarDescuentosPlanillaYDeclaracion(descuentos = []) {
  const planilla = [];
  const declaracionLiquidacion = [];
  (descuentos || []).forEach((c) => {
    if (esDescuentoSoloDeclaracion(c)) declaracionLiquidacion.push(c);
    else planilla.push(c);
  });
  const sum = (arr) => arr.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);
  return {
    planilla,
    declaracionLiquidacion,
    totalPlanilla: sum(planilla),
    totalDeclaracion: sum(declaracionLiquidacion),
  };
}

/**
 * Empleado listo para boleta ordinaria: sin conceptos solo-declaración
 * (gratificaciones / viáticos) y totales recalculados sin esos montos.
 * Conceptos 0406/0313 cargados manualmente se conservan.
 */
function listarConceptosBoletaGrupo(empleado, tipo) {
  const grouped = empleado?.conceptos?.[tipo];
  if (Array.isArray(grouped) && grouped.length) return grouped;
  const sep = empleado?.conceptos_separados?.[tipo];
  if (Array.isArray(sep) && sep.length) return sep;
  const flat = Array.isArray(empleado?.conceptos) ? empleado.conceptos : [];
  const needle = tipo === 'ingresos' ? 'ingreso' : tipo === 'descuentos' ? 'descuento' : 'aporte';
  return flat.filter((c) => String(c.tipo_concepto || '').toLowerCase().includes(needle));
}

export function empleadoParaBoletaOrdinaria(empleado) {
  if (!empleado) return empleado;
  const ingresos = filtrarConceptosBoletaOrdinaria(listarConceptosBoletaGrupo(empleado, 'ingresos'));
  const descuentos = filtrarDescuentosBoletaOrdinaria(listarConceptosBoletaGrupo(empleado, 'descuentos'));
  const aportes = listarConceptosBoletaGrupo(empleado, 'aportes');
  const totalIngresos = ingresos.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);
  const totalDescuentos = descuentos.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);
  const totalAportes = aportes.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);

  const next = {
    ...empleado,
    conceptos: {
      ...empleado.conceptos,
      ingresos,
      descuentos,
      aportes,
    },
    totales: {
      ...(empleado.totales || {}),
      totalIngresos,
      totalDescuentos,
      totalAportes,
      netoAPagar: totalIngresos - totalDescuentos,
    },
  };

  // Misma exclusión en boleta interna (viáticos/grati con destino interna)
  if (empleado.conceptosInternos || empleado.totalesInternos) {
    const ingresosInt = filtrarConceptosBoletaOrdinaria(empleado.conceptosInternos?.ingresos || []);
    const descuentosInt = empleado.conceptosInternos?.descuentos || [];
    const aportesInt = empleado.conceptosInternos?.aportes || [];
    const ti = ingresosInt.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);
    const td = descuentosInt.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);
    const ta = aportesInt.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);
    next.conceptosInternos = {
      ...(empleado.conceptosInternos || {}),
      ingresos: ingresosInt,
      descuentos: descuentosInt,
      aportes: aportesInt,
    };
    next.totalesInternos = {
      ...(empleado.totalesInternos || {}),
      totalIngresos: ti,
      totalDescuentos: td,
      totalAportes: ta,
      netoAPagar: ti - td,
    };
  }

  return next;
}

export function conceptosSoloDeclaracionGratificacion(empleado, boleta = 'ambas') {
  const plame = (empleado?.conceptos?.ingresos || []).filter(esConceptoDeclaracionGratificacion);
  const interna = (empleado?.conceptosInternos?.ingresos || []).filter(esConceptoDeclaracionGratificacion);
  if (boleta === 'plame') return plame;
  if (boleta === 'interna') return interna;
  return [...plame, ...interna];
}

export function conceptosSoloDeclaracionViatico(empleado, boleta = 'ambas') {
  const plame = (empleado?.conceptos?.ingresos || []).filter(esConceptoDeclaracionViatico);
  const interna = (empleado?.conceptosInternos?.ingresos || []).filter(esConceptoDeclaracionViatico);
  if (boleta === 'plame') return plame;
  if (boleta === 'interna') return interna;
  return [...plame, ...interna];
}

export function conceptosSoloDeclaracionMovilidad(empleado, boleta = 'ambas') {
  const plame = (empleado?.conceptos?.ingresos || []).filter(esConceptoDeclaracionMovilidadJatsa);
  const interna = (empleado?.conceptosInternos?.ingresos || []).filter(esConceptoDeclaracionMovilidadJatsa);
  if (boleta === 'plame') return plame;
  if (boleta === 'interna') return interna;
  return [...plame, ...interna];
}

const sumarConceptos = (arr = []) =>
  (arr || []).reduce((s, c) => s + (parseFloat(c.monto ?? c.valor) || 0), 0);

function destinoSoloInterna(concepto) {
  return String(concepto?.destino_ajuste || concepto?.destino || '').toLowerCase() === 'interna';
}

/** Solo ingesta/ajustes a PLAME. Un 0917 sin destino no es viático PLAME (suele ser interna). */
function destinoDeclaracionPlame(concepto) {
  const dest = String(concepto?.destino_ajuste || concepto?.destino || '').toLowerCase();
  return dest === 'plame' || dest === 'ambas';
}

/** Un monto por código (el mayor > 0). Evita 0917/0909 duplicados en dual/cierre. */
function sumarUnicoPorCodigo(lista, pred) {
  const porCodigo = new Map();
  (lista || []).forEach((c) => {
    if (!pred(c)) return;
    const monto = parseFloat(c?.monto ?? c?.valor) || 0;
    if (monto <= 0) return;
    const codigo = codigoDeConceptoPlame(c) || textoConceptoPlano(c) || 'decl';
    const prev = porCodigo.get(codigo) || 0;
    if (monto > prev) porCodigo.set(codigo, monto);
  });
  return [...porCodigo.values()].reduce((s, n) => s + n, 0);
}

/** Lista de tareos del empleado (vista abierta o cerrada). */
function obtenerTareosEmpleado(empleado) {
  if (!empleado) return [];
  if (Array.isArray(empleado.tareosEmpleadoFiltrados) && empleado.tareosEmpleadoFiltrados.length) {
    return empleado.tareosEmpleadoFiltrados;
  }
  if (Array.isArray(empleado.tareosEmpleado) && empleado.tareosEmpleado.length) {
    return empleado.tareosEmpleado;
  }
  if (Array.isArray(empleado.tareos?.tareosDetallados)) {
    return empleado.tareos.tareosDetallados;
  }
  if (Array.isArray(empleado.tareos)) {
    return empleado.tareos;
  }
  return [];
}

/**
 * Viáticos netamente desde tareos, separados por tipo_boleta.
 * - interna → solo Interna
 * - plame → solo PLAME
 * - (ambas se guardan como dos filas, una de cada tipo)
 * No usa conceptos de boleta calculada (evita PLAME fantasma).
 */
export function montosViaticosDesdeTareos(empleado) {
  let viaticosPlame = 0;
  let viaticosInterna = 0;
  obtenerTareosEmpleado(empleado).forEach((tareo) => {
    (tareo.conceptos || []).forEach((c) => {
      if (!esConceptoDeclaracionViatico(c)) return;
      const monto = parseFloat(c.monto) || 0;
      if (monto <= 0) return;
      if (conceptoTareoAplicaACanal(c, 'interna', tareo.conceptos, empleado)) {
        viaticosInterna += monto;
      }
      if (conceptoTareoAplicaACanal(c, 'plame', tareo.conceptos, empleado)) {
        viaticosPlame += monto;
      }
    });
  });
  return {
    viaticosPlame,
    viaticosInterna,
  };
}

/**
 * Montos de declaración separados por boleta (evita sumar PLAME+interna dos veces).
 * Viáticos PLAME: solo 0917 con destino plame/ambas (ingesta). Si no, tareo canal PLAME.
 * Un 0917 en ingresos PLAME sin destino es fantasma de interna y no se cuenta.
 * Movilidad: solo 0909 de Jatsa (no la 0909 pagada de tareo).
 */
export function montosDeclaracionPorBoleta(empleado) {
  const viaTareo = montosViaticosDesdeTareos(empleado);
  const viaPlameBoleta = sumarUnicoPorCodigo(
    empleado?.conceptos?.ingresos,
    (c) => esConceptoDeclaracionViatico(c) && destinoDeclaracionPlame(c),
  );
  const viaInternaBoleta = sumarUnicoPorCodigo(
    empleado?.conceptosInternos?.ingresos,
    esConceptoDeclaracionViatico,
  );
  return {
    gratiPlame: sumarConceptos(conceptosSoloDeclaracionGratificacion(empleado, 'plame')),
    gratiInterna: sumarConceptos(conceptosSoloDeclaracionGratificacion(empleado, 'interna')),
    viaticosPlame: viaPlameBoleta > 0 ? viaPlameBoleta : viaTareo.viaticosPlame,
    viaticosInterna: viaInternaBoleta > 0 ? viaInternaBoleta : viaTareo.viaticosInterna,
    movilidadPlame: sumarUnicoPorCodigo(
      empleado?.conceptos?.ingresos,
      (c) => esConceptoDeclaracionMovilidadJatsa(c) && !destinoSoloInterna(c),
    ),
    movilidadInterna: sumarUnicoPorCodigo(
      empleado?.conceptosInternos?.ingresos,
      esConceptoDeclaracionMovilidadJatsa,
    ),
  };
}

/**
 * Suma montos de viáticos / grati de un empleado.
 * Viáticos: desde tareos por destino. Grati: desde conceptos de boleta.
 * @param {'ambas'|'plame'|'interna'} boleta
 */
export function totalesDeclaracionEmpleado(empleado, boleta = 'ambas') {
  const decl = montosDeclaracionPorBoleta(empleado);
  const grati = conceptosSoloDeclaracionGratificacion(empleado, boleta);
  let totalViaticos = 0;
  let totalMovilidad = 0;
  if (boleta === 'plame') {
    totalViaticos = decl.viaticosPlame;
    totalMovilidad = decl.movilidadPlame;
  } else if (boleta === 'interna') {
    totalViaticos = decl.viaticosInterna;
    totalMovilidad = decl.movilidadInterna;
  } else {
    totalViaticos = decl.viaticosPlame + decl.viaticosInterna;
    totalMovilidad = decl.movilidadPlame + decl.movilidadInterna;
  }
  return {
    totalGratificaciones: sumarConceptos(grati),
    totalViaticos,
    totalMovilidad,
    empleadosConGrati: grati.length > 0 ? 1 : 0,
    empleadosConViatico: totalViaticos > 0 ? 1 : 0,
    empleadosConMovilidad: totalMovilidad > 0 ? 1 : 0,
  };
}

/**
 * Filas para Excel: conceptos informativos (grati / viáticos) que no entran al neto.
 * @param {object[]} empleadosCompletos - con conceptos (sin filtrar boleta ordinaria)
 * @param {'plame'|'interna'} boleta
 */
export function filasConceptosNoIngresanAlPago(empleadosCompletos = [], boleta = 'plame') {
  const filas = [];
  (empleadosCompletos || []).forEach((emp) => {
    const ingresos =
      boleta === 'interna'
        ? emp?.conceptosInternos?.ingresos || []
        : emp?.conceptos?.ingresos || [];
    (ingresos || []).forEach((c) => {
      if (!esConceptoSoloDeclaracion(c)) return;
      const monto = parseFloat(c.monto) || 0;
      if (monto <= 0) return;
      const codigo = String(c.codigoPlame || c.codigo || c.codigo_plame || '');
      const descripcion = c.descripcion || c.concepto || c.nombre || codigo || 'Sin descripción';
      const tipo = esConceptoDeclaracionViatico(c)
        ? 'Viáticos'
        : (esConceptoDeclaracionGratificacion(c)
          ? 'Gratificación'
          : (esConceptoDeclaracionMovilidadJatsa(c) ? 'Movilidad' : 'Solo declaración'));
      filas.push({
        dni: emp.documento || emp.numero_documento || '',
        empleado: emp.nombre || emp.nombre_completo || '',
        tipo,
        codigo,
        descripcion,
        monto,
      });
    });
  });
  return filas;
}

/**
 * Marca/inyecta 0406-0313 del módulo de gratificaciones como declaración PLAME
 * (no boleta). También inyecta la grati alternativa en conceptosInternos.
 * No toca liquidación ni conceptos manuales ajenos al módulo.
 */
export function aplicarDeclaracionGratificacionDesdeModulo(empleado) {
  if (!empleado) return empleado;
  const gratificaciones = Array.isArray(empleado.gratificaciones_planilla)
    ? empleado.gratificaciones_planilla
    : [];
  const liquidaciones = Array.isArray(empleado.liquidaciones_planilla)
    ? empleado.liquidaciones_planilla
    : [];
  const codigosReservadosLiquidacion = new Set();
  liquidaciones.forEach((liq) => {
    alinearCalculosLiquidacionEstaticos(liq).forEach((cc) => {
      if (esCalculoConceptoLiquidacionDescuento(cc)) return;
      const cod = codigoPlameNormalizado(cc.codigo_plame || cc.codigo);
      if ((parseFloat(cc.monto) || 0) > 0) codigosReservadosLiquidacion.add(cod);
    });
  });
  if (gratificaciones.length === 0) return empleado;

  const porCodigo = new Map();
  const porCodigoInterna = new Map();
  gratificaciones.forEach((grat) => {
    (grat.calculos_conceptos || []).forEach((cc) => {
      const codigo = String(cc.codigo_plame || '');
      const monto = parseFloat(cc.monto) || 0;
      if (!codigo || monto <= 0) return;
      if (codigosReservadosLiquidacion.has(codigoPlameNormalizado(codigo))) return;
      porCodigo.set(codigo, {
        codigo,
        monto,
        descripcion: cc.descripcion || codigo,
        id: cc.id,
      });
    });
    (grat.calculos_conceptos_interna || []).forEach((cc) => {
      const codigo = String(cc.codigo_plame || '');
      const monto = parseFloat(cc.monto) || 0;
      if (!codigo || monto <= 0) return;
      porCodigoInterna.set(codigo, {
        codigo,
        monto,
        descripcion: cc.descripcion || codigo,
        id: cc.id,
      });
    });
  });

  let next = empleado;

  if (porCodigo.size > 0) {
    const ingresosActuales = [...(next.conceptos?.ingresos || [])];
    const ingresos = ingresosActuales.map((ing) => {
      if (esConceptoLiquidacionPlanilla(ing)) return ing;
      const codigo = String(ing.codigoPlame || ing.codigo || '');
      if (!porCodigo.has(codigo)) return ing;
      return {
        ...ing,
        origenCalculo: 'gratificacion',
        origen_calculo: 'gratificacion',
        excluirBoletaOrdinaria: true,
        excluir_boleta_ordinaria: true,
        origen: ing.origen || 'gratificacion_declaracion',
      };
    });

    const codigosPresentes = new Set(
      ingresos.map((ing) => String(ing.codigoPlame || ing.codigo || ''))
    );
    porCodigo.forEach((dato, codigo) => {
      if (codigosPresentes.has(codigo)) return;
      ingresos.push({
        id: dato.id || `grati_${codigo}`,
        codigo,
        descripcion: dato.descripcion,
        monto: dato.monto,
        codigoPlame: codigo,
        base_calculo: 0,
        tipo_concepto: 'ingreso',
        origen: 'gratificacion_declaracion',
        excluirBoletaOrdinaria: true,
        excluir_boleta_ordinaria: true,
        origenCalculo: 'gratificacion',
        origen_calculo: 'gratificacion',
      });
    });

    next = {
      ...next,
      conceptos: {
        ...next.conceptos,
        ingresos,
      },
    };
  }

  if (porCodigoInterna.size > 0) {
    const baseInternos = next.conceptosInternos || { ingresos: [], descuentos: [], aportes: [] };
    const ingresosInternosActuales = [...(baseInternos.ingresos || [])];
    const ingresosInternos = ingresosInternosActuales.map((ing) => {
      if (esConceptoLiquidacionPlanilla(ing)) return ing;
      const codigo = String(ing.codigoPlame || ing.codigo || '');
      if (!porCodigoInterna.has(codigo)) return ing;
      return {
        ...ing,
        origenCalculo: 'gratificacion',
        origen_calculo: 'gratificacion',
        excluirBoletaOrdinaria: true,
        excluir_boleta_ordinaria: true,
        origen: ing.origen || 'gratificacion_declaracion_interna',
      };
    });

    const codigosInternosPresentes = new Set(
      ingresosInternos.map((ing) => String(ing.codigoPlame || ing.codigo || ''))
    );
    porCodigoInterna.forEach((dato, codigo) => {
      if (codigosInternosPresentes.has(codigo)) return;
      ingresosInternos.push({
        id: dato.id || `grati_int_${codigo}`,
        codigo,
        descripcion: dato.descripcion,
        monto: dato.monto,
        codigoPlame: codigo,
        base_calculo: 0,
        tipo_concepto: 'ingreso',
        origen: 'gratificacion_declaracion_interna',
        excluirBoletaOrdinaria: true,
        excluir_boleta_ordinaria: true,
        origenCalculo: 'gratificacion',
        origen_calculo: 'gratificacion',
      });
    });

    next = {
      ...next,
      conceptosInternos: {
        ...baseInternos,
        ingresos: ingresosInternos,
      },
    };
  }

  return next;
}

/** Montos congelados de liquidación (tabla + detalle), no recalcular en boleta. */
export function montosGratificacionLiquidacionEstaticos(liquidacion) {
  if (!liquidacion) return null;
  const det = liquidacion.detalle_calculos;
  const items =
    det?.gratificacion_trunca?.detalle
    || det?.gratificacion_detalle
    || liquidacion.gratificacion_detalle;
  if (Array.isArray(items) && items.length > 0) {
    let base = 0;
    let bonif = 0;
    items.forEach((g) => {
      base += Math.max(
        0,
        (parseFloat(g.gratificacion_bruta) || 0) - (parseFloat(g.descuento_treintavos) || 0)
      );
      bonif += parseFloat(g.bonificacion_9) || 0;
    });
    return {
      '0406': Math.round(base * 100) / 100,
      '0313': Math.round(bonif * 100) / 100,
    };
  }
  const baseExplicita = parseFloat(liquidacion.gratificacion_base);
  const bonifExplicita = parseFloat(liquidacion.gratificacion_bonificacion_9);
  if (baseExplicita > 0 || bonifExplicita > 0) {
    return {
      '0406': Math.round(baseExplicita * 100) / 100,
      '0313': Math.round(bonifExplicita * 100) / 100,
    };
  }
  const total = parseFloat(liquidacion.gratificacion_proporcional) || 0;
  if (total <= 0) return null;
  const baseNeta = Math.round((total / 1.09) * 100) / 100;
  return {
    '0406': baseNeta,
    '0313': Math.round((total - baseNeta) * 100) / 100,
  };
}

/** calculos_conceptos PLAME con montos de la liquidación guardada (estáticos). */
export function alinearCalculosLiquidacionEstaticos(liquidacion) {
  let calculos = [...(liquidacion?.calculos_conceptos || [])];
  const reemplazar = (codigo, monto, tipoConcepto, descripcion) => {
    const v = parseFloat(monto) || 0;
    if (v <= 0) return;
    calculos = calculos.filter(
      (c) => codigoPlameNormalizado(c.codigo_plame || c.codigo) !== codigoPlameNormalizado(codigo)
    );
    calculos.push({
      codigo_plame: codigoPlameNormalizado(codigo),
      tipo_concepto: tipoConcepto,
      monto: v,
      descripcion: descripcion || undefined,
      origen_calculo: 'liquidacion',
    });
  };

  reemplazar('0114', liquidacion.vacaciones_truncas, 'vacaciones', 'VACACIONES TRUNCAS');
  reemplazar('0904', liquidacion.cts, 'cts', 'COMPENSACION POR TIEMPO DE SERVICIOS');
  const montosGrati = montosGratificacionLiquidacionEstaticos(liquidacion);
  if (montosGrati) {
    reemplazar('0406', montosGrati['0406'], 'gratificacion', 'GRATIFICACIONES');
    reemplazar('0313', montosGrati['0313'], 'bonificacion_9', 'BONIFICACION EXTRAORDINARIA');
  }
  codigosDescuentoPensionVacacionesEstaticos(liquidacion).forEach((item) => {
    reemplazar(item.codigo, item.monto, 'descuento_pension', null);
  });
  return calculos;
}

/** Descuentos pensionarios vacaciones truncas desde detalle guardado (incl. prima seguro 0606 AFP). */
export function codigosDescuentoPensionVacacionesEstaticos(liquidacion) {
  const det =
    liquidacion?.detalle_calculos?.vacaciones_truncas?.descuento_pension
    || liquidacion?.descuento_pension_detalle;
  if (!det) return [];
  const byCodigo = new Map();
  (det.codigos_plame || []).forEach((item) => {
    const cod = codigoPlameNormalizado(item.codigo);
    const m = parseFloat(item.monto) || 0;
    if (m > 0) byCodigo.set(cod, { codigo: cod, monto: Math.round(m * 100) / 100 });
  });
  const onp = parseFloat(det.onp) || 0;
  const com = parseFloat(det.afp_comision) || 0;
  const seg = parseFloat(det.afp_seguro) || 0;
  const apo = parseFloat(det.afp_aporte) || 0;
  if (onp > 0) byCodigo.set('0606', { codigo: '0606', monto: Math.round(onp * 100) / 100 });
  if (com > 0) byCodigo.set('0601', { codigo: '0601', monto: Math.round(com * 100) / 100 });
  if (seg > 0) byCodigo.set('0606', { codigo: '0606', monto: Math.round(seg * 100) / 100 });
  if (apo > 0) byCodigo.set('0608', { codigo: '0608', monto: Math.round(apo * 100) / 100 });
  return [...byCodigo.values()];
}

/**
 * Añade ingresos/descuentos de liquidación (informativos, no neto) al detalle de boleta.
 * Corrige snapshots donde la pensión de vacaciones quedó en ingresos o faltó en descuentos.
 */
export function fusionarLiquidacionPlanillaEnConceptos(separados, opts = {}) {
  const liquidaciones = opts.liquidacionesPlanilla || [];
  const usuario = opts.conceptosUsuario || {};
  const catalogoPlame = opts.catalogoPlame || opts.conceptosPlame || [];
  let ingresos = [...(separados?.ingresos || [])];
  let descuentos = [...(separados?.descuentos || [])];

  const ingresosSinDescMalUbicados = [];
  ingresos.forEach((ing) => {
    if (esDescuentoLiquidacionPlanilla(ing)) {
      descuentos.push({
        ...ing,
        descripcion: ing.descripcion || ing.concepto || ing.nombre,
        tipo_concepto: ing.tipo_concepto || 'descuento_pension',
        origenCalculo: 'liquidacion',
        origen_calculo: 'liquidacion',
        excluirBoletaOrdinaria: true,
        excluir_boleta_ordinaria: true,
      });
    } else {
      ingresosSinDescMalUbicados.push(ing);
    }
  });
  ingresos = ingresosSinDescMalUbicados;

  const quitarSoloDescuentosLiquidacionPorCodigo = (lista, codigo) => {
    const codNorm = codigoPlameNormalizado(codigo);
    return lista.filter((d) => {
      if (codigoPlameNormalizado(d.codigoPlame || d.codigo) !== codNorm) return true;
      return !esDescuentoLiquidacionPlanilla(d);
    });
  };

  const agregarDesdeCalculo = (cc) => {
    const codigo = String(cc.codigo_plame || '');
    const monto = parseFloat(cc.monto) || 0;
    if (!codigo || monto <= 0) return;
    const fila = {
      id: cc.id || `liq_desc_${codigoPlameNormalizado(codigo)}`,
      codigo,
      codigo_plame: codigo,
      codigoPlame: codigo,
      descripcion: descripcionPlameParaLinea(codigo, cc, catalogoPlame),
      monto,
      base_calculo: 0,
      origen: 'liquidacion',
      origenCalculo: 'liquidacion',
      origen_calculo: 'liquidacion',
      excluirBoletaOrdinaria: true,
      excluir_boleta_ordinaria: true,
    };
    if (esCalculoConceptoLiquidacionDescuento(cc)) {
      fila.tipo_concepto = 'descuento_pension';
      descuentos = quitarSoloDescuentosLiquidacionPorCodigo(descuentos, codigo);
      descuentos.push(fila);
    } else {
      fila.tipo_concepto = 'ingreso';
      ingresos = ingresos.filter(
        (i) => codigoPlameNormalizado(i.codigoPlame || i.codigo) !== codigoPlameNormalizado(codigo)
      );
      ingresos.push(fila);
    }
  };

  liquidaciones.forEach((liq) => {
    const calculosEstaticos = alinearCalculosLiquidacionEstaticos(liq);
    const codigosReemplazar = new Set(
      calculosEstaticos
        .filter((cc) => !esCalculoConceptoLiquidacionDescuento(cc))
        .map((cc) => codigoPlameNormalizado(cc.codigo_plame || cc.codigo))
        .filter((c) => c && c !== '0000')
    );
    if (codigosReemplazar.size > 0) {
      ingresos = ingresos.filter((ing) => {
        const cod = codigoPlameNormalizado(ing.codigoPlame || ing.codigo);
        return !codigosReemplazar.has(cod);
      });
    }
    calculosEstaticos.forEach(agregarDesdeCalculo);
  });

  (usuario.descuentos || []).forEach((d) => {
    if (!esDescuentoLiquidacionPlanilla(d)) return;
    const codigo = String(d.codigoPlame || d.codigo || '');
    if (
      descuentos.some(
        (x) => String(x.codigoPlame || x.codigo || '') === codigo && esDescuentoLiquidacionPlanilla(x)
      )
    ) {
      return;
    }
    descuentos.push({
      ...d,
      codigoPlame: codigo,
      excluirBoletaOrdinaria: true,
      excluir_boleta_ordinaria: true,
    });
  });

  (usuario.ingresos || []).forEach((ing) => {
    if (!esConceptoLiquidacionPlanilla(ing)) return;
    const codigo = String(ing.codigoPlame || ing.codigo || '');
    if (
      ingresos.some(
        (x) => String(x.codigoPlame || x.codigo || '') === codigo && esConceptoLiquidacionPlanilla(x)
      )
    ) {
      return;
    }
    ingresos.push({
      ...ing,
      excluirBoletaOrdinaria: true,
      excluir_boleta_ordinaria: true,
    });
  });

  return {
    ingresos,
    descuentos,
    aportes: separados?.aportes || [],
  };
}

function pareceIngresoLiquidacionPorTexto(concepto) {
  const t = textoConceptoPlano(concepto);
  if (!t) return false;
  if (t.includes('vacaciones trunc')) return true;
  if (t.includes('compensacion') && t.includes('tiempo') && t.includes('servicios')) return true;
  if (t.includes('gratificaciones') && t.includes('fiestas patrias')) return true;
  if (t.includes('bonificacion extraordinaria') && (t.includes('proporcional') || t.includes('29351'))) {
    return true;
  }
  return false;
}

/** Totales de boleta ordinaria (sin ingresos/descuentos solo declaración). */
export function recalcularTotalesBoletaOrdinaria(conceptos) {
  const ingresos = conceptos?.ingresos || [];
  const descuentos = conceptos?.descuentos || [];
  const aportes = conceptos?.aportes || [];
  const totalIngresos = ingresos
    .filter((c) => !esConceptoSoloDeclaracion(c))
    .reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);
  const totalDescuentos = descuentos
    .filter((c) => !esDescuentoSoloDeclaracion(c))
    .reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);
  const totalAportes = aportes.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);
  return {
    totalIngresos,
    totalDescuentos,
    totalAportes,
    netoAPagar: totalIngresos - totalDescuentos,
  };
}

/**
 * Snapshot planilla cerrada / pre-cierre: etiqueta liquidación y recalcula neto como planilla abierta.
 */
export function normalizarConceptosSnapshotPlanillaCerrada(conceptos, opts = {}) {
  const liquidaciones = opts.liquidacionesPlanilla || [];
  const gratificaciones = opts.gratificacionesPlanilla || [];
  const codigosLiq = new Set();
  liquidaciones.forEach((liq) => {
    alinearCalculosLiquidacionEstaticos(liq).forEach((cc) => {
      const c = String(cc.codigo_plame || '');
      if (c) codigosLiq.add(c);
    });
  });
  const codigosGrati = new Set();
  const codigosLiqEstaticos = new Set();
  liquidaciones.forEach((liq) => {
    alinearCalculosLiquidacionEstaticos(liq).forEach((cc) => {
      const c = codigoPlameNormalizado(cc.codigo_plame || cc.codigo);
      if (c && c !== '0000') codigosLiqEstaticos.add(c);
    });
  });
  gratificaciones.forEach((g) => {
    (g.calculos_conceptos || []).forEach((cc) => {
      const c = codigoPlameNormalizado(cc.codigo_plame || cc.codigo);
      const m = parseFloat(cc.monto) || 0;
      if (!c || c === '0000' || m <= 0) return;
      if (codigosLiqEstaticos.has(c)) return;
      codigosGrati.add(c);
    });
  });
  const hayLiquidacion = liquidaciones.length > 0 || Boolean(opts.fechaCeseActual);

  let base = fusionarLiquidacionPlanillaEnConceptos(conceptos, {
    liquidacionesPlanilla: liquidaciones,
    conceptosUsuario: opts.conceptosUsuario,
    catalogoPlame: opts.catalogoPlame || opts.conceptosPlame,
  });

  base = {
    ...base,
    ingresos: (base.ingresos || []).map((ing) => {
      const codigo = String(ing.codigoPlame || ing.codigo || ing.codigo_plame || '');
      if (ing.origenCalculo === 'liquidacion' || ing.origen_calculo === 'liquidacion') {
        return {
          ...ing,
          origenCalculo: 'liquidacion',
          origen_calculo: 'liquidacion',
          excluirBoletaOrdinaria: true,
          excluir_boleta_ordinaria: true,
        };
      }
      if (codigosLiq.has(codigo)) {
        return {
          ...ing,
          origenCalculo: 'liquidacion',
          origen_calculo: 'liquidacion',
          excluirBoletaOrdinaria: true,
          excluir_boleta_ordinaria: true,
        };
      }
      if (codigosGrati.has(codigo) && !codigosLiq.has(codigo)) {
        return {
          ...ing,
          origenCalculo: 'gratificacion',
          excluirBoletaOrdinaria: true,
          excluir_boleta_ordinaria: true,
        };
      }
      if (hayLiquidacion && pareceIngresoLiquidacionPorTexto(ing)) {
        return {
          ...ing,
          origenCalculo: 'liquidacion',
          origen_calculo: 'liquidacion',
          excluirBoletaOrdinaria: true,
          excluir_boleta_ordinaria: true,
        };
      }
      return ing;
    }),
  };

  return {
    conceptos: base,
    totales: recalcularTotalesBoletaOrdinaria(base),
  };
}
